/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Injectable} from '@angular/core';
import {Observable, of} from 'rxjs';

import {AdnHttpProgressCallback} from '../../components/util/adn-http.service';
import {createAdnUploadFormData} from '../../components/util/adn-http.service';
import {AdnApiClientService} from '../common/controller/adn-api-client.service';
import {
  SignalAdminResourceApiBase,
  SignalAdminResourceModel,
  SignalAdminResourcePage
} from '../common/controller/signal-admin-resource-api';

export interface ThirdPartyAudienceModel extends SignalAdminResourceModel {
  [key: string]: any;
}

@Injectable({providedIn: 'root'})
export class ThirdPartyAudienceApiService extends SignalAdminResourceApiBase<ThirdPartyAudienceModel> {
  constructor(client: AdnApiClientService) {
    super(client, 'thirdpartyaudiences');
  }

  searchForTargeting(params: Record<string, any> | string[]): Observable<SignalAdminResourcePage<ThirdPartyAudienceModel>> {
    if (Array.isArray(params) && !params.length) {
      return of({results: [], totalCount: 0, page: 0});
    }
    const queryParams = Array.isArray(params)
      ? {id: params.slice(0, 1001).join(';'), pageSize: Math.min(params.length, 1001)}
      : params;
    return this.client.request({
      method: 'GET',
      url: this.resourceUri('thirdpartyaudiences'),
      params: {
        includeHidden: false,
        includeActive: true,
        includeInactive: false,
        orderBy: 'name',
        pageSize: 1001,
        ...queryParams
      }
    });
  }

  uploadPostcodes(id: string, postcodes: string[], countryIsoCode: string): Observable<any> {
    return this.client.request({
      method: 'POST',
      url: this.resourceUri(['thirdpartyaudiences', id, 'postcodes'].join('/')),
      body: {postcodes, countryIsoCode}
    });
  }

  uploadAudienceDefinitions(
    file: File,
    provider: string,
    onProgress?: AdnHttpProgressCallback
  ): Observable<any> {
    return this.client.upload({
      method: 'POST',
      url: this.resourceUri('thirdpartyaudiences'),
      params: {
        delimiter: ',',
        externalReferenceField: 0,
        providerField: -1,
        nameField: '1',
        descriptionField: 2,
        objectStateField: -1,
        teamField: 3,
        objectState: 'ACTIVE',
        provider
      },
      body: createAdnUploadFormData({file})
    }, onProgress);
  }

  uploadAudienceData(
    file: File,
    provider: string,
    countryIsoCode: string,
    onProgress?: AdnHttpProgressCallback
  ): Observable<any> {
    return this.client.upload({
      method: 'POST',
      url: this.resourceUri('thirdpartyaudiences/postcodes'),
      params: {
        delimiter: ',',
        format: 'POSTCODE_LIST',
        provider,
        countryIsoCode
      },
      body: createAdnUploadFormData({file})
    }, onProgress);
  }
}
